import _ from 'lodash';
import { SgSessionStorageService, SgSubmissionUtil } from 'saga-modules';
import CommonConstants from 'sg-common-constants';
import { CustomQuoteService } from 'gw-capability-quoteandbind';

/**
 * @param {array} coveragesArray
 * @param {string} coverageCode
 * @returns {object} returns coverage with given pattern code
 */
function getCoverageByCode(coveragesArray, coverageCode) {
    return _.find(coveragesArray, { patternCode: coverageCode });
}

/**
 * @param {object} coverage
 * @param {string} termCode
 * @returns {object} returns term with given pattern code
 */
function getTermByCode(coverage, termCode) {
    return _.find(coverage.terms, { patternCode: termCode });
}

/**
 * @param {object} term
 * @param {string} optionCode
 * @returns {object} returns term option with given code
 */
function getTermOptionByCode(term, optionCode) {
    return _.find(term.options, { code: optionCode });
}

function getCustomQuote(submission) {
    if (submission.quoteData && submission.quoteData.offeredQuotes) {
        return _.find(submission.quoteData.offeredQuotes, { isCustom: true });
    }
    return undefined;
}

function getQuote(submission) {
    if (submission.quoteData && submission.quoteData.offeredQuotes) {
        if (submission.bindData && submission.bindData.chosenQuote) {
            return _.find(submission.quoteData.offeredQuotes, { publicID: submission.bindData.chosenQuote });
        }
        return getCustomQuote(submission);
    }
    return undefined;
}

function getAnnualPaymentPlan(modelVM) {
    // make sure we're always prioritizing ANNUAL_UPFRONT payment plan.
    return modelVM.bindData.paymentPlans && (_.find(modelVM.bindData.paymentPlans.value,
        (paymentPlan) => paymentPlan.name === CommonConstants.PAYMENT_PLANS.ANNUAL_UPFRONT)
        || _.find(modelVM.bindData.paymentPlans.value,
            (paymentPlan) => paymentPlan.name === CommonConstants.PAYMENT_PLANS.ANNUAL));
}

function getAnnualPremium(modelVM) {
    const plan = getAnnualPaymentPlan(modelVM);
    if (plan) {
        return plan.total.amount.toFixed(2);
    }
    return '';
}

function getMonthlyPaymentPlan(modelVM) {
    return _.find(modelVM.bindData.paymentPlans.value,
        (paymentPlan) => paymentPlan.name === CommonConstants.PAYMENT_PLANS.MONTHLY);
}

function getQuarterlyPaymentPlan(modelVM) {
    return _.find(modelVM.bindData.paymentPlans.value,
        (paymentPlan) => paymentPlan.name === CommonConstants.PAYMENT_PLANS.QUARTERLY);
}

function getMonthlyUpfrontPaymentPlan(modelVM) {
    return _.find(modelVM.bindData.paymentPlans.value,
        (paymentPlan) => paymentPlan.name === CommonConstants.PAYMENT_PLANS.MONTHLY_UPFRONT);
}

function getSelectedPaymentPlan(modelVM) {
    const paymentId = SgSessionStorageService.getItem(SgSessionStorageService.KEY.SELECTED_PAYMENT_ID)
        || modelVM.quoteData.offeredQuotes.value[0].requestedPaymentPlanId_Ext;
    return _.find(modelVM.bindData.paymentPlans.value,
        (paymentPlan) => paymentPlan.billingId === paymentId);
}

const isMonthlyPlanSelected = (modelVM) => {
    const paymentPlan = getSelectedPaymentPlan(modelVM);
    return paymentPlan && CommonConstants.MONTHLY_PAYMENT_PLANS_ID.includes(paymentPlan.billingId);
};

const isQuarterlyPlanSelected = (modelVM) => {
    const paymentPlan = getSelectedPaymentPlan(modelVM);
    return paymentPlan && paymentPlan.name === CommonConstants.PAYMENT_PLANS.QUARTERLY;
};

const getPaymentPlanByBillingId = (modelVM, billingId) => {
    return _.find(modelVM.bindData.paymentPlans.value,
        (paymentPlan) => paymentPlan.billingId === billingId);
};

function isAnnualPlanSelected(modelVM) {
    const paymentPlan = getSelectedPaymentPlan(modelVM);
    return paymentPlan && (paymentPlan.name === CommonConstants.PAYMENT_PLANS.ANNUAL
        || paymentPlan.name === CommonConstants.PAYMENT_PLANS.ANNUAL_UPFRONT);
}

const isMonthlyAllowed = (modelVM) => {
    return _.some(_.get(modelVM, 'bindData.paymentPlans.value'), (pp) => pp.numberOfInstallments_Ext > 1);
};

const isResolQuote = (modelVM) => {
    return !_.isNil(modelVM.isResolQuote_Ext) && modelVM.isResolQuote_Ext.value === true;
};

const isQuoted = (modelVM) => {
    return !_.isNil(modelVM.baseData) && modelVM.baseData.policyPeriodStatus_Ext.value === 'Quoted';
};
const getAccountHolder = (modelVM) => {
    const productCode = modelVM.baseData.productCode.value || modelVM.baseData.productCode;
    if (productCode === CommonConstants.MOTOR_PRODUCT_CODE) {
        return modelVM.baseData.motorAccountHolder;
    }
    if (productCode === CommonConstants.HOME_PRODUCT_CODE) {
        return modelVM.baseData.homeAccountHolder;
    }
    if (modelVM.baseData.productCode.value === CommonConstants.PRIVATE_MEDICAL_INSURANCE_CODE) {
        return modelVM.baseData.smiAccountHolder;
    }
    if (modelVM.baseData.productCode.value === CommonConstants.TRAVEL_PRODUCT_CODE) {
        return modelVM.baseData.sptAccountHolder;
    }
    return null;
};

const isAggregatorQuote = (modelVM) => {
    return modelVM.aggregatorDetailsDTO_Ext && modelVM.aggregatorDetailsDTO_Ext.isAggregatorQuote_Ext.value;
};

const isIndicativeQuote = (modelVM) => {
    if (!_.isNil(modelVM.quotationType_Ext) && !_.isNil(modelVM.quotationType_Ext.value)) {
        return modelVM.quotationType_Ext.value.code === CommonConstants.QUOTATION_TYPE.INDICATIVE_QUOTE;
    }
    return false;
};

const isIndicativeToFullQuote = (modelVM) => {
    if (!_.isNil(modelVM.quotationType_Ext) && !_.isNil(modelVM.quotationType_Ext.value)) {
        return modelVM.quotationType_Ext.value.code === CommonConstants.QUOTATION_TYPE.INDICATIVE_TO_FULL_QUOTE;
    }
    return false;
};

const getNumberOfMonthlyInstallments = (modelVM) => {
    const monthlyPP = getMonthlyPaymentPlan(modelVM);
    return monthlyPP.numberOfInstallments_Ext;
};

const getNumberOfInstallmentsByBillingId = (modelVM, billingId) => {
    const paymentPlan = getPaymentPlanByBillingId(modelVM, billingId);
    return paymentPlan.numberOfInstallments_Ext;
};

const getNumberOfQuarterlyInstallments = (modelVM) => {
    const quarterlyPP = getQuarterlyPaymentPlan(modelVM);
    return _.get(quarterlyPP, 'numberOfInstallments_Ext');
};

const updatePaymentPlan = (props) => {
    const {
        modelVM, updateData, setQuoting, coverages
    } = props;
    setQuoting(true);
    const newModelVM = _.clone(modelVM);
    newModelVM.quoteData.offeredQuotes.value[0].requestedPaymentPlanId_Ext = SgSubmissionUtil.getSelectedPaymentPlan(newModelVM).billingId;
    const customQuoteDTO = {
        sessionUUID: newModelVM.sessionUUID.value,
        quoteID: newModelVM.quoteID.value,
        quote: newModelVM.quoteData.offeredQuotes.value[0],
        coverages: coverages
    };
    return CustomQuoteService.updateCustomQuote(customQuoteDTO).then((customQuote) => {
        _.merge(newModelVM.value, customQuote);
        updateData(newModelVM);
    }).finally(() => {
        setQuoting(false);
    });
};

function isDdAprCapApplied(modelVM) {
    return modelVM.bindData.ddAprCap.value;
}
const isBound = (modelVM) => {
    return modelVM.baseData.policyPeriodStatus_Ext.value === CommonConstants.STATUS.BOUND;
};

const handleCustomerPaymentAbilityError = () => {
    const err = { appErrorCode: CommonConstants.SG_ERROR_CODES.SAGA_DECLINE };
    const baseErrorData = { appData: CommonConstants.SG_ERROR_REASONS.ABILITY_TO_PAY_ERROR };
    _.set(err, 'baseError.data', baseErrorData);
    throw err;
};

export default {
    getCoverageByCode,
    getTermByCode,
    getCustomQuote,
    getQuote,
    getAnnualPaymentPlan,
    getAnnualPremium,
    isBound,
    isMonthlyAllowed,
    getMonthlyPaymentPlan,
    getMonthlyUpfrontPaymentPlan,
    getQuarterlyPaymentPlan,
    getSelectedPaymentPlan,
    isMonthlyPlanSelected,
    isQuarterlyPlanSelected,
    isAnnualPlanSelected,
    getPaymentPlanByBillingId,
    isResolQuote,
    isQuoted,
    getAccountHolder,
    isAggregatorQuote,
    isIndicativeQuote,
    isIndicativeToFullQuote,
    updatePaymentPlan,
    getTermOptionByCode,
    isDdAprCapApplied,
    getNumberOfMonthlyInstallments,
    getNumberOfQuarterlyInstallments,
    getNumberOfInstallmentsByBillingId,
    handleCustomerPaymentAbilityError
};
